import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  Observable,
  Subscription as RxSubscription,
  filter,
  map,
  of,
  startWith,
  take,
  tap
} from 'rxjs';
import { PermissionsService } from '../../auth/permissions/permissions.service';
import { selectSelectedCompany } from '../../auth/store/auth.selectors';
import { Company, CompanyDataType } from '../../auth/utility/auth.models';
import { onUntouched } from '../../shared/loading-state/loadable';
import { selectSubscription } from '../../shared/subscription/store/subscription.selectors';
import { Subscription } from '../../shared/subscription/subscription.models';
import { getVehicleList } from '../../shared/vehicle/store/vehicle.actions';
import { selectVehicles } from '../../shared/vehicle/store/vehicle.selectors';
import { NavigationElement } from './sidenav.models';
import { SidenavService } from './sidenav.service';
import { DEFAULT_NAVIGATION, EXTERNAL_LINKS, GET_STARTED_NAVIGATION } from './sidenav.static';

/**
 * Sidenav domestic Pipe that checks if a NavigationElement is currently active
 * by comparing the `link` member of the element with the current Router URL
 */
@Pipe({
  name: 'isActive',
  standalone: false,
})
export class IsPageActivePipe implements PipeTransform {
  constructor(private readonly router: Router) {}

  transform(page: NavigationElement): Observable<boolean> {
    if (!page) return of(false);

    return this.router.events.pipe(
      // listen to router events emitted at the navigation end only
      filter(routerEvent => routerEvent instanceof NavigationEnd),
      // start with the current router state,
      // since first NavigationEnd event emits before this gets called on the very first time
      startWith(this.router),
      map(() => {
        return (
          // check if the page is active
          this.router.url.split('?')[0] === page.link ||
          // or if the page has children and one of those children is active
          (page.subPages !== undefined &&
            page.subPages.length > 0 &&
            page.subPages.some(subPage => this.router.url.split('?')[0] === subPage.link))
        );
      })
    );
  }
}

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  providers: [IsPageActivePipe],
  standalone: false,
})
export class SidenavComponent implements OnInit, OnDestroy {
  private readonly _rxSubscription = new RxSubscription();

  protected readonly DataType = CompanyDataType;

  protected navigationPages = DEFAULT_NAVIGATION;
  protected getStartedPages = GET_STARTED_NAVIGATION;
  protected externalLinks = EXTERNAL_LINKS;

  protected hasVehicles = true;
  protected getStartedClicked = false;

  protected subscription!: Subscription;
  protected company?: Company;

  protected activeSubMenus = new Set<string>();

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly router: Router,
    private readonly store: Store,
    private readonly isActive: IsPageActivePipe,
    private readonly permissionService: PermissionsService,
    protected readonly sidenav: SidenavService,
    protected readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Check if any sub-menu should be opened on element init
    for (const page of this.navigationPages) {
      if (page.subPages !== undefined) {
        this.isActive
          .transform(page)
          .pipe(
            take(1),
            tap(isActive => {
              if (isActive) this.activeSubMenus.add(page.name);
            })
          )
          .subscribe();
      }
    }

    const subscription$ = this.store
      .select(selectSubscription)
      .pipe(tap(subscription => (this.subscription = subscription.value!)));
    this._rxSubscription.add(subscription$.subscribe());

    const company$ = this.store
      .select(selectSelectedCompany)
      .pipe(tap(company => (this.company = company!)));
    this._rxSubscription.add(company$.subscribe());

    const vehicleList$ = this.store.select(selectVehicles).pipe(
      onUntouched(() => this.store.dispatch(getVehicleList())),
      tap(vehicles => {
        this.hasVehicles = !!vehicles.value && vehicles.value.length > 0;
        this.changeDetector.markForCheck();
      })
    );
    this._rxSubscription.add(vehicleList$.subscribe());

    if (this.router.url.includes('setup-wizard')) this.getStartedClicked = true;
    else this.getStartedClicked = false;

    // Move monitoring down when not subscribed
    if (this.subscription.isFree && this.company?.data_type === CompanyDataType.FM) {
      [this.navigationPages[0], this.navigationPages[1]] = [this.navigationPages[1], this.navigationPages[0]];
      this.activeSubMenus.add(this.navigationPages[1].name);
    }

    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    this._rxSubscription.unsubscribe();
  }

  handleGetStartedClick() {
    void this.router.navigate(['admin', 'setup-wizard']);
  }

  toggleSubMenu(page: NavigationElement): void {
    let hasPermissions = true;
    if (page.requirePermissions) {
      this.permissionService.hasPermissions(page.requirePermissions)
        .pipe(take(1))
        .subscribe(result => hasPermissions = result);
    }

    if (
      page.disabled ||
      !hasPermissions
    ) return;

    if (this.activeSubMenus.has(page.name)) this.activeSubMenus.delete(page.name);
    else this.activeSubMenus.add(page.name);

    this.activeSubMenus = new Set(this.activeSubMenus);
  }

  onSmallerItemClick(action: (dialog: MatDialog) => void): void {
    action(this.dialog);
  }

  getQueryParamsHandling(page: NavigationElement): 'preserve' | null {
    return page.link?.startsWith('/monitoring/') ? null : 'preserve';
  }
}
